import validator from 'validator'

// --------------------------------------------------
// Regex patterns centralised here
// --------------------------------------------------
const namePattern = /^[A-Za-z'\- ]+$/
const companyNamePattern = /^[A-Za-z0-9'\-\s&.]+$/
const phonePattern = /^[0-9()\s\-+]+$/
const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/
const gtmPattern = /^GTM-[A-Z0-9]+$/

/*
 * Facade around the `validator` library so consuming code only
 * depends on this public surface area.  If we ever swap the
 * underlying library, the rest of the app remains untouched.
 */
export const Validators = {
  /** Owner / member number: 9–11 numeric characters */
  isMemberNumber: (value: string): boolean => value !== '' && validator.isLength(value, { min: 9, max: 11 }) && validator.isNumeric(value),

  /** First name pattern */
  isFirstName: (value: string): boolean => value !== '' && validator.matches(value, namePattern),

  /** Last name pattern */
  isLastName: (value: string): boolean => value !== '' && validator.matches(value, namePattern),

  /** Generic person-name pattern (letters, spaces, hyphens, apostrophes) */
  isName: (value: string): boolean => value !== '' && validator.matches(value, namePattern),

  /** Company name pattern */
  isCompanyName: (value: string): boolean => value !== '' && validator.matches(value, companyNamePattern),

  /**
   * Phone number with locale-aware validation
   * Accepts either ISO-2 (e.g., US, CA) or ISO-3 (e.g., USA, CAN) country codes
   */
  /*
   * Phone numbers often contain formatting (spaces, dashes, parentheses, country code
   * symbols).  To prevent perfectly valid numbers such as "(555) 123-4567" or
   * "+1 555-123-4567" from failing validation, we strip all non-digit characters,
   * then normalise based on the selected locale before delegating to
   * validator.isMobilePhone.
   */
  isPhone: (value: string, countryCode: string): boolean => {
    if (value === '') return false

    // Remove everything except digits so formats like (555) 123-4567 pass
    const digitsOnly = value.replace(/\D/g, '')
    if (digitsOnly === '') return false

    // Country code is required for locale-aware validation
    if (!countryCode || countryCode.trim() === '') return false

    const code = countryCode.toUpperCase()

    let locale: 'en-US' | 'en-CA' | 'any'
    switch (code) {
      case 'US':
      case 'USA':
        locale = 'en-US'
        break
      case 'CA':
      case 'CAN':
        locale = 'en-CA'
        break
      default:
        locale = 'any'
    }

    // Normalise number for validator.js.  For US/CA we expect the library to work
    // with either 10-digit local numbers or E.164 +1XXXXXXXXXX.  If only 10 digits
    // were supplied, prefix with +1 so both validator and backend agree.
    let normalised = digitsOnly
    if ((locale === 'en-US' || locale === 'en-CA') && digitsOnly.length === 10) {
      normalised = `+1${digitsOnly}`
    } else if (!normalised.startsWith('+')) {
      // For other locales, prefix '+' if missing so validator treats it as intl.
      normalised = `+${digitsOnly}`
    }

    // Use validator.js for all validation - it handles locale-specific rules
    const strictValid = validator.isMobilePhone(normalised, locale)
    if (strictValid) return true

    // Fallback for international numbers: allow 7-15 digits (ITU-T E.164 standard)
    if (locale === 'any' && digitsOnly.length >= 7 && digitsOnly.length <= 15) {
      return true
    }

    return false
  },

  /** Contract number: alphanumeric ≤12 chars */
  isContractNumber: (value: string): boolean => value !== '' && validator.isLength(value, { min: 1, max: 12 }) && validator.isAlphanumeric(value),

  /**
   * Postal code validation with country-specific rules
   * @param value - The postal code to validate
   * @param countryCode - The country code (US, CA, etc.)
   * @returns true if valid
   */
  isPostalCode: (value: string, countryCode: string): boolean => {
    if (value === '') return false

    const code = countryCode.toUpperCase()
    const trimmedValue = value.trim()

    // US postal code rules: numeric only, allows hyphens, up to 10 characters
    // Supports both 5-digit and 9-digit ZIP+4 formats
    if (code === 'US' || code === 'USA') {
      // Accepts: 12345 or 12345-6789 or 123456789 (9 digits without hyphen)
      return /^(\d{5}(-\d{4})?|\d{9})$/.test(trimmedValue)
    }

    // International postal code rules: alphanumeric, allows hyphens and spaces, up to 12 characters
    // Note: Some countries (like East Timor) don't have official postal codes, but we allow flexible formats
    if (!/^[A-Za-z0-9\s\-]+$/.test(trimmedValue)) return false
    return trimmedValue.length <= 12
  },

  /** Strong password: min length + lowercase + uppercase + number */
  isStrongPassword: (value: string, minLength = 8): boolean => {
    const v = String(value || '')
    if (v.length < minLength) return false
    if (!/[a-z]/.test(v)) return false
    if (!/[A-Z]/.test(v)) return false
    if (!/\d/.test(v)) return false
    return true
  },

  isValidEmail: (value: string): boolean => emailPattern.test(value),

  isValidGTMId: (value: string): boolean => gtmPattern.test(value),
}

export type ValidatorKeys = keyof typeof Validators
